import { useState, useRef, useEffect } from "react";
import { Bell, X } from "lucide-react";
import {
    useUser,
    useNotifications,
    useUnreadNotifications,
    useMarkNotificationRead,
} from "../Use-auth";
import { useQueryClient } from "@tanstack/react-query";

export default function Notifications() {
    const { data: user } = useUser();
    const userId = user?.userId;
    const queryClient = useQueryClient();

    const { data: notifications = [] } = useNotifications(userId);
    const { data: unreadNotifications = [] } = useUnreadNotifications(userId);
    const markNotificationRead = useMarkNotificationRead();
    const [loadingId, setLoadingId] = useState(null);

    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const [expandedMap, setExpandedMap] = useState({});

    const toggleExpand = (id) => {
        setExpandedMap((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const truncateText = (text, limit = 25) => {
        if (!text) return "";
        return text.length > limit ? text.slice(0, limit) + "..." : text;
    };

    const handleMarkRead = (notificationId) => {
        if (!userId || !notificationId) return;

        setLoadingId(notificationId);

        queryClient.setQueryData(["notifications", userId], (old = []) =>
            old.map((n) =>
                n.id === notificationId ? { ...n, isRead: true } : n
            )
        );

        queryClient.setQueryData(["unreadNotifications", userId], (old = []) =>
            old.filter((n) => n.id !== notificationId)
        );

        markNotificationRead.mutate(
            { userId, notificationId },
            {
                onError: () => {
                    queryClient.invalidateQueries(["notifications", userId]);
                    queryClient.invalidateQueries(["unreadNotifications", userId]);
                },
                onSettled: () => {
                    setLoadingId(null);
                },
            }
        );
    };

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        if (showDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showDropdown]);

    // Favicon update based on unread notifications
    const prevUnreadRef = useRef(unreadNotifications.length);
    useEffect(() => {
        const favicon = document.querySelector("link[rel='icon']");
        if (!favicon) return;

        favicon.href = unreadNotifications.length > 0 ? "/notifications-icon.webp" : "/HubSyntax.webp";
    }, [unreadNotifications.length]);

    // Play sound for new notifications
    useEffect(() => {
        if (unreadNotifications.length > prevUnreadRef.current) {
            const audio = new Audio("/preview.mp3");
            audio.volume = 0.6;
            audio.play().catch(() => { });
        }
        prevUnreadRef.current = unreadNotifications.length;
    }, [unreadNotifications.length]);

    const sortedNotifications = [...notifications].sort((a, b) => {
        if (a.isRead !== b.isRead) {
            return a.isRead ? 1 : -1;
        }

        return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setShowDropdown((prev) => !prev)}
                className="relative p-0 sm:p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
            >
                <Bell size={24} className="text-gray-700" />
                {unreadNotifications.length > 0 && (
                    <span className="absolute -top-[1px] -right-[2px] bg-red-600 text-white text-[10px] min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full font-medium">
                        {unreadNotifications.length > 9 ? "9+" : unreadNotifications.length}
                    </span>
                )}
            </button>

            <div
                className={`custom-scrollbar absolute right-0 mt-2 w-72 sm:w-80 bg-white border border-gray-200 shadow-xl rounded-xl p-2 max-h-[400px] overflow-y-auto z-[9999]
        transition-all duration-300 ease-out transform
        ${showDropdown ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}
      `}
            >
                <div className="flex justify-between items-center mb-2 px-2">
                    <h3 className="font-semibold text-gray-800 text-sm sm:text-base">Notifications</h3>
                    <button onClick={() => setShowDropdown(false)} className="hover:bg-gray-200 rounded-full p-1 transition">
                        <X size={16} />
                    </button>
                </div>

                {notifications.length === 0 ? (
                    <p className="text-center text-gray-500 py-2 text-sm">No notifications</p>
                ) : (
                    sortedNotifications.map((n) => (
                        <div
                            key={n.id}
                            className={`p-2 mb-2 border rounded-lg cursor-pointer flex justify-between items-start hover:shadow-md transition-all duration-200
                ${n.isRead ? "bg-gray-50" : "bg-gray-100"}
              `}
                        >
                            <div className="pr-2">
                                <p className="text-xs font-medium text-gray-800"> {n.title}</p>
                                <p
                                    className="text-[10px] text-gray-600 cursor-pointer hover:text-gray-900 transition w-full break-all"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(n.id);
                                    }}
                                >
                                    {(expandedMap[n.id] || n.isRead)
                                        ? n.message
                                        : truncateText(n.message, 25)}
                                </p>
                                <p className="text-[9px] text-gray-400 mt-1">
                                    {new Date(n.createdAt).toLocaleString()}
                                </p>
                            </div>
                            {!n.isRead && (
                                <button
                                    className="ml-2 text-xs text-blue-600 whitespace-nowrap hover:underline transition flex items-center gap-1"
                                    onClick={() => handleMarkRead(n.id)}
                                    disabled={loadingId === n.id}
                                >
                                    {loadingId === n.id ? (
                                        <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
                                    ) : (
                                        "Mark as read"
                                    )}
                                </button>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}